import { useEffect, useState } from 'react';
import { MessageSquarePlus, Bug, Lightbulb, HelpCircle, X } from 'lucide-react';
import { Btn, Input, Select, Alert, Badge } from './UI';
import { useToast } from './Toast';
import { feedbackApi } from '../services/api';
import { collectContext, contextCounts } from '../services/diagnostics';

/* Hibajelentő / javaslat widget (2026-08-02, admin F4).
 *
 * Tervezési elv (Sentry User Feedback minta): a jó widget NEM kérdez, hanem
 * gyűjt. A felhasználótól csak azt kérjük, amit tényleg csak ő tud — mit
 * csinált, mit várt —, a technikai kontextust (útvonal, verzió, böngésző,
 * konzolhibák, sikertelen API-hívások, session replay link) automatikusan
 * csatoljuk.
 *
 * A csatolt adatot a beküldés előtt MEGMUTATJUK: a felhasználó lássa, mit küld
 * el magáról. Ez nem formalitás — bizalmi kérdés.
 */

const KINDS = [
  { value: 'BUG', label: 'Hiba', icon: Bug, hint: 'Valami nem úgy működik, ahogy kellene' },
  { value: 'IDEA', label: 'Ötlet', icon: Lightbulb, hint: 'Javaslat, ami megkönnyítené a munkát' },
  { value: 'QUESTION', label: 'Kérdés', icon: HelpCircle, hint: 'Nem világos, hogyan kell csinálni' },
];

const SEVERITIES = [
  { value: 'LOW', label: 'Alacsony – zavaró, de megkerülhető' },
  { value: 'MEDIUM', label: 'Közepes – lassítja a munkát' },
  { value: 'HIGH', label: 'Magas – nem tudom elvégezni a feladatot' },
  { value: 'CRITICAL', label: 'Kritikus – az egész rendszer használhatatlan' },
];

export default function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState('BUG');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [severity, setSeverity] = useState('MEDIUM');
  const [showContext, setShowContext] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const toast = useToast();

  const counts = contextCounts();

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // M12 (2026-09-05): a cégválasztó „Aktiválás" modalja ezen az eseményen át
  // nyitja meg a widgetet előre kitöltött típussal/címmel — a widget marad az
  // egyetlen kapcsolat-csatorna a frontendben (nincs külön support-e-mail).
  useEffect(() => {
    const onOpen = (e) => {
      const d = e?.detail || {};
      if (d.kind && KINDS.some(k => k.value === d.kind)) setKind(d.kind);
      if (d.title) setTitle(String(d.title));
      if (d.body) setBody(String(d.body));
      setOpen(true);
    };
    window.addEventListener('pf:open-feedback', onOpen);
    return () => window.removeEventListener('pf:open-feedback', onOpen);
  }, []);

  function reset() {
    setKind('BUG'); setTitle(''); setBody(''); setSeverity('MEDIUM');
    setShowContext(false); setError(null);
  }

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await feedbackApi.create({ kind, title: title.trim(), body: body.trim() || null,
                                 severity, ...collectContext() });
      toast.success('Köszönjük! A bejelentést rögzítettük.');
      setOpen(false); reset();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        title="Hiba jelentése vagy ötlet küldése"
        aria-label="Visszajelzés küldése"
        style={{
          position: 'fixed', right: '1rem', bottom: '1rem', zIndex: 60,
          display: 'flex', alignItems: 'center', gap: '0.4rem',
          padding: '0.5rem 0.8rem', borderRadius: 999,
          background: 'var(--bg-card)', color: 'var(--text)',
          border: '1px solid var(--border)', boxShadow: 'var(--shadow)',
          cursor: 'pointer', fontSize: 'var(--fs-sm)',
        }}>
        <MessageSquarePlus size={15} />
        Visszajelzés
      </button>
    );
  }

  const KindIcon = KINDS.find((k) => k.value === kind)?.icon || Bug;

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
               zIndex: 100, display: 'grid', placeItems: 'end end', padding: '1rem' }}>
      <div role="dialog" aria-modal="true" aria-label="Visszajelzés küldése"
           style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow)',
                    width: 'min(420px, 100%)', maxHeight: '88vh',
                    display: 'flex', flexDirection: 'column' }}>
        <header style={{ padding: '0.8rem 1rem', borderBottom: '1px solid var(--border)',
                         display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <KindIcon size={16} style={{ color: 'var(--primary)' }} />
          <b style={{ fontSize: 'var(--fs-lg)' }}>Visszajelzés</b>
          <button onClick={() => setOpen(false)} aria-label="Bezárás"
                  style={{ marginLeft: 'auto', background: 'none', border: 0,
                           color: 'var(--text-muted)', cursor: 'pointer' }}>
            <X size={16} />
          </button>
        </header>

        <form onSubmit={submit}
              style={{ padding: '1rem', overflowY: 'auto', display: 'flex',
                       flexDirection: 'column', gap: '0.7rem' }}>
          <Alert type="danger">{error}</Alert>

          <div style={{ display: 'flex', gap: '0.35rem' }}>
            {KINDS.map((k) => (
              <button key={k.value} type="button" onClick={() => setKind(k.value)}
                      title={k.hint}
                      style={{
                        flex: 1, padding: '0.4rem', borderRadius: 'var(--radius)',
                        cursor: 'pointer', fontSize: 'var(--fs-sm)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        gap: '0.3rem',
                        border: `1px solid ${kind === k.value ? 'var(--primary)' : 'var(--border)'}`,
                        background: kind === k.value ? 'var(--primary-subtle)' : 'transparent',
                        color: kind === k.value ? 'var(--text)' : 'var(--text-muted)',
                      }}>
                <k.icon size={13} /> {k.label}
              </button>
            ))}
          </div>

          <Input label="Mi történt? *" value={title} required maxLength={200}
                 onChange={(e) => setTitle(e.target.value)}
                 placeholder={kind === 'IDEA'
                   ? 'Pl. „Havi zárás emlékeztető e-mailben”'
                   : 'Pl. „A kivonat-feltöltés időtúllépéssel elszáll”'} />

          <div>
            <label style={{ display: 'block', fontSize: 'var(--fs-xs)',
                            textTransform: 'uppercase', letterSpacing: '0.06em',
                            color: 'var(--text-muted)', marginBottom: '0.2rem' }}>
              Részletek
            </label>
            <textarea rows={4} value={body} maxLength={8000}
                      onChange={(e) => setBody(e.target.value)}
                      placeholder="Mit csináltál, mit vártál, mi történt helyette?"
                      style={{ width: '100%', background: 'var(--bg-input)',
                               border: '1px solid var(--border)',
                               borderRadius: 'var(--radius)', color: 'var(--text)',
                               padding: '0.4rem 0.5rem', fontSize: 'var(--fs-sm)',
                               fontFamily: 'inherit', resize: 'vertical' }} />
          </div>

          {kind === 'BUG' && (
            <Select label="Mennyire akadályoz?" value={severity}
                    onChange={(e) => setSeverity(e.target.value)}
                    options={SEVERITIES} />
          )}

          {/* Átláthatóság: a felhasználó lássa, mit küld el magáról. */}
          <div style={{ border: '1px solid var(--border-dim)',
                        borderRadius: 'var(--radius)', padding: '0.5rem 0.6rem' }}>
            <button type="button" onClick={() => setShowContext((s) => !s)}
                    style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer',
                             color: 'var(--text-muted)', fontSize: 'var(--fs-xs)',
                             display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              {showContext ? '▾' : '▸'} Automatikusan csatolt adatok
              <Badge variant="default">{counts.consoleErrors} hiba</Badge>
              <Badge variant="default">{counts.failedRequests} kérés</Badge>
            </button>
            {showContext && (
              <div style={{ marginTop: '0.4rem', fontSize: 'var(--fs-xs)',
                            color: 'var(--text-muted)', lineHeight: 1.7 }}>
                <div>· Az oldal címe, ahol vagy</div>
                <div>· Böngésző, képernyőméret, app-verzió</div>
                <div>· Az utolsó {counts.consoleErrors} böngésző-hibaüzenet</div>
                <div>· Az utolsó {counts.failedRequests} sikertelen kérés
                  (<b>csak</b> a művelet neve és a hibakód — adat nem)</div>
                <div>· A képernyő-felvétel linkje, ha fut</div>
                <div style={{ marginTop: '0.3rem', color: 'var(--text-dim)' }}>
                  Számla-adat, összeg és bankszámlaszám nem kerül bele.
                </div>
              </div>
            )}
          </div>

          <Btn type="submit" variant="primary" size="md" loading={busy}
               disabled={title.trim().length < 3}>
            Elküldöm
          </Btn>
        </form>
      </div>
    </div>
  );
}
